import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import { Button, toastCaller } from '@/shared/ui';

import { useCommunityTemplate, useRefreshTemplateList } from '../hooks';

import styles from './CommunityVersionOffer.module.scss';
import { InlineConfirm } from './InlineConfirm';
import { TemplatePreviewContent } from './TemplatePreview';

type Props = {
  templateId: string;
  communityTemplateId: string;
  canEdit: boolean;
  viewing: boolean;
  className?: string;
  onViewingChange: (viewing: boolean) => void;
};

const CommunityVersionPreview: FC<{ communityTemplateId: string }> = ({
  communityTemplateId,
}) => {
  const { template, loading, failed } =
    useCommunityTemplate(communityTemplateId);

  return (
    <section data-component="workspace-community-version-preview">
      {template && <TemplatePreviewContent template={template} />}
      {loading && (
        <p className={cn('p3', styles.caption)}>
          <FormattedMessage id="global.loading" defaultMessage="Loading…" />
        </p>
      )}
      {failed && (
        <p className={cn('p3', styles.caption)} role="alert">
          <FormattedMessage
            id="workspace.templates.libraryPreviewFailed"
            defaultMessage="The template could not be loaded."
          />
        </p>
      )}
    </section>
  );
};

// Dismissing hides the offer only until the community publishes a later
// version: scribe keeps the dismissed version and offers the next one.
export const CommunityVersionOffer: FC<Props> = ({
  templateId,
  communityTemplateId,
  canEdit,
  viewing,
  className,
  onViewingChange,
}) => {
  const { formatMessage } = useIntl();
  const refreshList = useRefreshTemplateList();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const request = { templateId, communityTemplateId };

  const take = async () => {
    setPending(true);
    try {
      await templatesApi.takeCommunityVersion(request);
      toastCaller({
        type: 'success',
        message: formatMessage({
          id: 'workspace.templates.communityVersion.taken',
          defaultMessage: 'The template now holds the community version',
        }),
      });
      onViewingChange(false);
      await refreshList();
    } catch {
      toastCaller({
        type: 'error',
        message: formatMessage({
          id: 'workspace.templates.communityVersion.takeFailed',
          defaultMessage: 'Could not take the community version',
        }),
      });
    } finally {
      setPending(false);
      setConfirming(false);
    }
  };

  const dismiss = async () => {
    setPending(true);
    try {
      await templatesApi.dismissCommunityVersion(request);
      onViewingChange(false);
      await refreshList();
    } catch {
      toastCaller({
        type: 'error',
        message: formatMessage({
          id: 'workspace.templates.communityVersion.dismissFailed',
          defaultMessage: 'Could not hide the notice',
        }),
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <div
        className={cn(styles.notice, className)}
        data-component="workspace-community-version"
      >
        <p className="p3">
          <FormattedMessage
            id="workspace.templates.communityVersion.notice"
            defaultMessage="A newer version is available in the community"
          />
        </p>
        <div className={styles.actions}>
          <Button
            size="small"
            variant="secondary"
            data-component="workspace-community-version-view"
            onClick={() => onViewingChange(!viewing)}
          >
            {viewing ? (
              <FormattedMessage
                id="workspace.templates.communityVersion.back"
                defaultMessage="Back to my template"
              />
            ) : (
              <FormattedMessage
                id="workspace.templates.communityVersion.view"
                defaultMessage="View"
              />
            )}
          </Button>
          {canEdit && (
            <>
              <Button
                size="small"
                disabled={pending || confirming}
                data-component="workspace-community-version-take"
                onClick={() => setConfirming(true)}
              >
                <FormattedMessage
                  id="workspace.templates.communityVersion.take"
                  defaultMessage="Take the new version"
                />
              </Button>
              <Button
                size="small"
                variant="tertiary"
                disabled={pending}
                data-component="workspace-community-version-dismiss"
                onClick={dismiss}
              >
                <FormattedMessage
                  id="workspace.templates.communityVersion.dismiss"
                  defaultMessage="Hide"
                />
              </Button>
            </>
          )}
        </div>
      </div>
      {confirming && (
        <InlineConfirm
          className={styles.confirm}
          message={
            <FormattedMessage
              id="workspace.templates.communityVersion.confirm"
              defaultMessage="The community version becomes the clinic's next version of this template, and the clinic's own changes to it are replaced. Notes already recorded keep the version they were made with."
            />
          }
          confirmLabel={
            <FormattedMessage
              id="workspace.templates.communityVersion.take"
              defaultMessage="Take the new version"
            />
          }
          pending={pending}
          onConfirm={take}
          onCancel={() => setConfirming(false)}
        />
      )}
      {viewing && (
        <CommunityVersionPreview communityTemplateId={communityTemplateId} />
      )}
    </>
  );
};
